import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, CheckCircle, FileText, Pen, ShieldCheck } from 'lucide-react';
import { SignaturePad } from '@/pages/LoanContractPage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicSignPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signing, setSigning] = useState(false);
  const [done, setDone] = useState(false);

  const fetchContract = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/public/contracts/sign/${token}`);
      setData(res.data);
      if (res.data.already_signed) setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Link inválido ou expirado');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchContract(); }, [fetchContract]);

  const handleSign = async (signatureData) => {
    setSigning(true);
    try {
      await axios.post(`${API_URL}/api/public/contracts/sign/${token}`, { signature_data: signatureData });
      toast.success('Contrato assinado com sucesso!');
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha ao assinar');
    } finally {
      setSigning(false);
    }
  };

  const fmtBRL = (v) => `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4">
        <Card className="bg-[#111] border-white/10 max-w-md w-full" data-testid="public-sign-error">
          <CardContent className="py-12 text-center space-y-3">
            <FileText className="w-10 h-10 text-zinc-700 mx-auto" />
            <p className="text-zinc-300 font-medium">{error}</p>
            <p className="text-zinc-500 text-sm">Entre em contato com o remetente para obter um novo link.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const roleLabel = data.role === 'lender' ? 'MUTUANTE (Credor)' : 'MUTUÁRIO (Devedor)';

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-8">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <FileText className="w-7 h-7 text-white" />
          <div>
            <h1 className="text-xl font-bold text-white">Contrato de Mútuo</h1>
            <p className="text-zinc-500 text-sm">Nº {data.contract_number} • {data.contract_date}</p>
          </div>
        </div>

        <Card className="bg-[#111] border-white/10">
          <CardContent className="p-6 sm:p-8 space-y-6">
            <div className="flex items-center justify-between border-b border-white/10 pb-4">
              <div>
                <p className="text-sm text-zinc-400">Você está assinando como</p>
                <p className="text-white font-semibold">{data.party_name}</p>
                <Badge className="mt-1 bg-white/10 text-zinc-300 text-xs">{roleLabel}</Badge>
              </div>
              <div className="text-right">
                <p className="text-xs text-zinc-500">Valor</p>
                <p className="text-white font-mono">{fmtBRL(data.amount)}</p>
              </div>
            </div>

            {/* Clauses */}
            <div className="space-y-4 max-h-[45vh] overflow-y-auto pr-2" data-testid="public-clauses">
              {data.clauses?.map(([heading, body], i) => (
                <div key={i} className="space-y-1">
                  {heading && <h3 className="text-sm font-bold text-zinc-200">{heading}</h3>}
                  <p className="text-sm text-zinc-400 leading-relaxed whitespace-pre-line">{body}</p>
                </div>
              ))}
            </div>

            {/* Signature */}
            <div className="border-t border-white/10 pt-6">
              {done ? (
                <div className="text-center py-6 space-y-2" data-testid="public-sign-done">
                  <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto" />
                  <p className="text-white font-medium">Assinatura registrada</p>
                  <p className="text-zinc-500 text-sm">Obrigado. Este contrato foi assinado eletronicamente por você.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-white font-medium">
                    <Pen className="w-4 h-4" /> Assine no campo abaixo
                  </div>
                  {signing ? (
                    <div className="flex items-center gap-2 text-zinc-400 py-4">
                      <Loader2 className="w-4 h-4 animate-spin" /> Enviando assinatura...
                    </div>
                  ) : (
                    <SignaturePad
                      signerLabel={`Assinatura de ${data.party_name}`}
                      onSave={handleSign}
                    />
                  )}
                </div>
              )}
            </div>

            <p className="text-[11px] text-zinc-600 text-center pt-2 flex items-center justify-center gap-1">
              <ShieldCheck className="w-3 h-3" />
              Documento com validade jurídica nos termos da Lei nº 14.063/2020.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
